import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
  useTab,
} from '../../components/common/tabs/tab-container';
import Title from '../../components/common/title';
import { DOCUMENT_STORAGE_INDEX } from '../../router/routes';
import { DocumentStorageTypes } from '../../enums/document-storage-type';
import css from './index.module.css';
import CreateFolder from './create-folder';
import { useService } from '../../services/services';
import { useParams } from 'react-router-dom';
import { can } from '../../services/utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const tabConfig = [
  {
    key: DocumentStorageTypes.WAREHOUSE,
    label: 'pages.document_storage.warehouse',
    ability: { entity_type: 'document_storage_warehouse' },
  },
  {
    key: DocumentStorageTypes.CROP,
    label: 'pages.document_storage.crop',
    ability: { entity_type: 'document_storage_crop' },
  },
  {
    key: DocumentStorageTypes.LIVESTOCK,
    label: 'pages.document_storage.livestock',
    ability: { entity_type: 'document_storage_livestock' },
  },
  {
    key: DocumentStorageTypes.MY_DOCUMENTS,
    label: 'pages.document_storage.my_documents',
    ability: { entity_type: 'document_storage_my_documents' },
  },
];

export interface IStorageUsageData {
  farmUsedStorage: number;
  farmStorageLimit: number;
}

export default function DocumentStorage() {
  const { t } = useTranslation();
  const [currentTab, setTab] = useTab(DOCUMENT_STORAGE_INDEX, [
    'tab',
    'farmId',
  ]);
  const { farmId } = useParams<{ [key: string]: string }>();
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  const [storageUsageData, setStorageUsageData] = useState<IStorageUsageData>({
    farmUsedStorage: 0,
    farmStorageLimit: 2,
  });
  const { farmService } = useService();
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    const subscription = farmService
      .getStorageUsage(farmId)
      .subscribe((usageData) => {
        setStorageUsageData(usageData);
      });
    return () => subscription.unsubscribe();
  }, [farmService, farmId, folders]);

  return (
    <>
      <div className={css['title-bar']}>
        <Title title={t('pages.document_storage.title')} />
        <div>
          {t('pages.document_storage.total_used_storage_amount', {
            usedPercent:
              (storageUsageData.farmUsedStorage /
                storageUsageData.farmStorageLimit) *
              100,
            usedAmount: storageUsageData.farmUsedStorage,
            farmAmountLimit: storageUsageData.farmStorageLimit,
          })}
        </div>
      </div>
      <TabContainer
        selectedTabKey={currentTab}
        setTab={setTab}
        tabNavigation={
          <TabNavigation>
            {tabConfig.map(
              ({ key, label, ability }) =>
                can(farmUser, ability) && (
                  <TabLink key={key} tabKey={key}>
                    {t(label)}
                  </TabLink>
                )
            )}
          </TabNavigation>
        }
        tabContents={
          <TabContents customClass={css.document_container}>
            {tabConfig.map(({ key, ability }) => (
              <Tab key={key} tabKey={key}>
                {can(farmUser, ability) && (
                  <CreateFolder folders={folders} setFolders={setFolders} />
                )}
              </Tab>
            ))}
          </TabContents>
        }
      />
    </>
  );
}
